import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getReportFilterOptions, deleteSavedReport, getSavedReports,
  saveReport as apiSaveReport, shareReport, shareGlobalReport, getSharableUsers,
  generateReport as apiGenerateReport, runSqlQuery,
  saveSqlQuery, getSavedSqlQueries, deleteSavedSqlQuery,
  shareSqlQuery, runSavedSqlQuery,
} from '../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── constants ────────────────────────────────────────────────────────────────

const DIMENSIONS = [
  { value: 'staff',          label: 'Staff' },
  { value: 'payment_mode',   label: 'Payment Mode' },
  { value: 'product',        label: 'Product / Service' },
  { value: 'customer',       label: 'Customer' },
  { value: 'booking_status', label: 'Booking Status' },
  { value: 'classify_a',     label: 'Classify A' },
  { value: 'classify_b',     label: 'Classify B' },
  { value: 'age_group',      label: 'Age Group' },
  { value: 'date',           label: 'Date' },
];

const FILTER_DIMS = [
  { value: 'staff',          label: 'Staff' },
  { value: 'payment_mode',   label: 'Payment Mode' },
  { value: 'product',        label: 'Product / Service' },
  { value: 'customer',       label: 'Customer' },
  { value: 'booking_status', label: 'Booking Status' },
  { value: 'classify_a',     label: 'Classify A' },
  { value: 'classify_b',     label: 'Classify B' },
  { value: 'age_group',      label: 'Age Group' },
];

const MEASURES = [
  { value: 'revenue',          label: 'Revenue' },
  { value: 'transactions',     label: 'Transactions' },
  { value: 'unique_customers', label: 'Unique Customers' },
  { value: 'avg_value',        label: 'Avg Revenue' },
  { value: 'commission',       label: 'Commission' },
  { value: 'bookings',         label: 'Bookings' },
];

const CHART_TYPES = [
  { value: 'bar',   label: 'Bar',   icon: '📊' },
  { value: 'line',  label: 'Line',  icon: '📈' },
  { value: 'table', label: 'Table', icon: '🗃️' },
];

const DATE_PERIODS = [
  { value: 'daily',   label: 'Daily'   },
  { value: 'weekly',  label: 'Weekly'  },
  { value: 'monthly', label: 'Monthly' },
];

const COLOR_BY_DIMS = [
  { value: 'staff',          label: 'Staff' },
  { value: 'payment_mode',   label: 'Payment Mode' },
  { value: 'product',        label: 'Product' },
  { value: 'customer',       label: 'Customer' },
  { value: 'booking_status', label: 'Booking Status' },
  { value: 'classify_a',     label: 'Classify A' },
  { value: 'classify_b',     label: 'Classify B' },
  { value: 'age_group',      label: 'Age Group' },
];

const CHART_COLORS = [
  '#2563eb','#16a34a','#d97706','#dc2626','#7c3aed',
  '#0891b2','#ea580c','#4f46e5','#059669','#b45309',
  '#db2777','#0284c7',
];

// ─── MultiSelect ──────────────────────────────────────────────────────────────

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [pending, setPending] = useState(selected);
  const ref = useRef(null);

  useEffect(() => { setPending(selected); }, [selected]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle   = (v) => setPending(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);
  const filtered = options.filter(o => String(o).toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ position:'relative', width:'100%' }}>
      <button onClick={() => setOpen(o => !o)} style={st.msBtn(pending.length > 0)}>
        <span>{label}{pending.length ? ` (${pending.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={st.msDrop}>
          <div style={{ padding:'6px 8px', borderBottom:'1px solid #e5e7eb' }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()} style={st.msSearch} />
          </div>
          <div style={{ padding:'4px 8px', borderBottom:'1px solid #e5e7eb', display:'flex', gap:'8px' }}>
            <button onClick={() => setPending(options.map(String))} style={st.msAct('#2563eb')}>All</button>
            <button onClick={() => setPending([])}                   style={st.msAct('#dc2626')}>Clear</button>
          </div>
          <div style={{ maxHeight:'160px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:'#9ca3af' }}>No results</p>}
            {filtered.map((opt, i) => {
              const v = String(opt); const isSel = pending.includes(v);
              return (
                <div key={i} onClick={() => toggle(v)} style={st.msItem(isSel)}>
                  <span style={st.msChk(isSel)}>{isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}</span>
                  <span style={{ fontSize:'12px' }}>{v}</span>
                </div>
              );
            })}
          </div>
          <div style={{ padding:'6px 8px', borderTop:'1px solid #e5e7eb', display:'flex', justifyContent:'flex-end' }}>
            <button onClick={() => { onChange(pending); setOpen(false); }} style={st.msApply}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 8);

const fmtVal = (v) => {
  const n = Number(v || 0);
  return n >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits:0 }) : String(parseFloat(n.toFixed(2)));
};

const downloadCSV = (reportData, dimension, measure) => {
  let csv = '';
  if (reportData.type === 'series') {
    csv = ['Label', ...reportData.series, 'Total'].join(',') + '\n';
    reportData.data.forEach(row => {
      const vals  = reportData.series.map(s => Number(row[s] || 0).toFixed(2));
      const total = reportData.series.reduce((sum, s) => sum + (row[s] || 0), 0);
      csv += ['"' + String(row.label).replace(/"/g, '""') + '"', ...vals, total.toFixed(2)].join(',') + '\n';
    });
  } else {
    csv = 'Label,Value\n';
    (reportData.data || []).forEach(row => {
      csv += ['"' + String(row.label).replace(/"/g, '""') + '"', Number(row.value || 0).toFixed(2)].join(',') + '\n';
    });
  }
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `report_${dimension}_${measure}_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

const downloadSqlCSV = (columns, allData) => {
  let csv = columns.map(c => `"${c}"`).join(',') + '\n';
  allData.forEach(row => {
    csv += row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',') + '\n';
  });
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `sql_query_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ─── Share Modal (boss only) ───────────────────────────────────────────────────

const ShareModal = ({ report, onClose, onSaved }) => {
  const [managers, setManagers]   = useState([]);
  const [selected, setSelected]   = useState(report.shared_with_users || []);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    getSharableUsers()
      .then(r => setManagers(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await shareReport(report.id, selected);
      onSaved(report.id, selected);
      onClose();
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <div style={st.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={st.modalBox}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
          <p style={{ fontSize:'14px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>
            Share "{report.report_name}"
          </p>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#9ca3af' }}>✕</button>
        </div>
        <p style={{ fontSize:'12px', color:'#6b7280', margin:'0 0 12px' }}>
          Select managers who can view this report:
        </p>
        {loading
          ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>Loading...</p>
          : managers.length === 0
            ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>
                No managers found in this tenant.
              </p>
            : managers.map(m => (
              <div key={m.user_id} onClick={() => toggle(m.user_id)}
                style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px',
                  borderRadius:'8px', cursor:'pointer', marginBottom:'4px',
                  background: selected.includes(m.user_id) ? '#ede9fe' : '#f9fafb',
                  border: `1px solid ${selected.includes(m.user_id) ? '#7c3aed' : '#e5e7eb'}` }}>
                <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0,
                  background: selected.includes(m.user_id) ? '#7c3aed' : 'transparent',
                  border: `2px solid ${selected.includes(m.user_id) ? '#7c3aed' : '#d1d5db'}`,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {selected.includes(m.user_id) && <span style={{ color:'#fff', fontSize:'9px' }}>✓</span>}
                </div>
                <div>
                  <p style={{ fontSize:'13px', fontWeight:'600', color:'#1f2937', margin:0 }}>{m.full_name}</p>
                  <p style={{ fontSize:'11px', color:'#9ca3af', margin:0 }}>{m.role} · @{m.username}</p>
                </div>
              </div>
            ))
        }
        <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', cursor:'pointer', color:'#4b5563', fontWeight:'500' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex:1, padding:'10px', background:'#7c3aed', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#fff', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : '✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Manager View (read-only shared reports + shared SQL queries) ─────────────

const ManagerView = () => {
  const { user }                         = useAuth();
  const isBoss                           = user?.role === 'boss';

  const [reports, setReports]           = useState([]);
  const [sqlQueries, setSqlQueries]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setManagerTab]      = useState('reports');
  const [expanded, setExpanded]         = useState({});
  const [reportData, setReportData]     = useState({});
  const [loadingId, setLoadingId]       = useState(null);
  const [sqlResults, setSqlResults]     = useState({});
  const [runningSql, setRunningSql]     = useState(null);
  const [sqlErrors, setSqlErrors]       = useState({});

  // Share state (boss only)
  const [shareModal, setShareModal]     = useState(null); // { type:'report'|'query', item }
  const [shareManagers, setShareMgrs]   = useState([]);
  const [shareSelected, setShareSel]    = useState([]);
  const [shareSaving, setShareSaving]   = useState(false);
  const [shareMgrLoading, setShareMgrL] = useState(false);

  useEffect(() => {
    Promise.all([
      getSavedReports().then(r => setReports(r.data.data || [])).catch(() => {}),
      getSavedSqlQueries().then(r => setSqlQueries(r.data.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const toggleReport = async (r) => {
    const isOpen = expanded[r.id];
    setExpanded(e => ({ ...e, [r.id]: !isOpen }));
    if (!isOpen && !reportData[r.id]) {
      setLoadingId(r.id);
      try {
        const fj = r.filter_json || {};
        const params = { dimension: r.x_field, measure: r.y_field };
        if (fj.date_from)   params.date_from = fj.date_from;
        if (fj.date_to)     params.date_to   = fj.date_to;
        if (fj.date_period && r.x_field === 'date') params.period = fj.date_period;
        if (fj.color_by)    { params.color_by = fj.color_by; if (fj.color_by_values?.length) params.color_by_values = fj.color_by_values.join(','); }
        (fj.filters || []).forEach(f => { if (f.dimension && f.values?.length) params[`ef_${f.dimension}`] = f.values.join(','); });
        const res = await apiGenerateReport(params);
        setReportData(d => ({ ...d, [r.id]: res.data }));
      } catch { /* silent */ }
      finally { setLoadingId(null); }
    }
  };

  const runSharedQuery = async (q) => {
    setRunningSql(q.id);
    setSqlErrors(e => ({ ...e, [q.id]: '' }));
    try {
      const res = await runSavedSqlQuery(q.id);
      setSqlResults(r => ({ ...r, [q.id]: res.data }));
    } catch (err) {
      setSqlErrors(e => ({ ...e, [q.id]: err.response?.data?.error || 'Failed to run query.' }));
    } finally { setRunningSql(null); }
  };

  const openShare = async (type, item) => {
    setShareModal({ type, item });
    setShareSel(item.shared_with_users || []);
    setShareMgrL(true);
    try { const r = await getSharableUsers(); setShareMgrs(r.data.data || []); }
    catch { /* silent */ } finally { setShareMgrL(false); }
  };

  const handleShareSave = async () => {
    if (!shareModal) return;
    setShareSaving(true);
    try {
      const { type, item } = shareModal;
      if (type === 'report') {
        if (item.is_global) {
          // Global report: boss shares with their managers via the global-specific endpoint
          await shareGlobalReport(item.global_report_id, shareSelected);
        } else {
          await shareReport(item.id, shareSelected);
        }
        setReports(rs => rs.map(r => r.id === item.id ? { ...r, shared_with_users: shareSelected } : r));
      } else {
        await shareSqlQuery(item.id, shareSelected);
        setSqlQueries(qs => qs.map(q => q.id === item.id ? { ...q, shared_with_users: shareSelected } : q));
      }
      setShareModal(null);
    } catch { /* silent */ } finally { setShareSaving(false); }
  };

  if (loading) return <div style={{ padding:'32px', textAlign:'center', color:'#9ca3af' }}>Loading...</div>;

  const hasReports = reports.length > 0;
  const hasQueries = sqlQueries.length > 0;

  if (!hasReports && !hasQueries) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'300px', color:'#9ca3af', textAlign:'center', gap:'8px' }}>
      <p style={{ fontSize:'40px', margin:0 }}>📭</p>
      <p style={{ fontSize:'14px' }}>No reports or queries have been shared with you yet.</p>
    </div>
  );

  return (
    <div style={{ paddingBottom:'32px' }}>
      {/* Tab bar */}
      <div style={{ display:'flex', borderBottom:'2px solid #e5e7eb', marginBottom:'0' }}>
        {[['reports', `📊 Reports${hasReports ? ` (${reports.length})` : ''}`],
          ['queries', `🛢 SQL Queries${hasQueries ? ` (${sqlQueries.length})` : ''}`]
        ].map(([key, label]) => (
          <button key={key} onClick={() => setManagerTab(key)} style={{
            flex:1, padding:'12px 8px', border:'none', background:'none', cursor:'pointer',
            fontSize:'13px', fontWeight:'600',
            color: activeTab === key ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === key ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom:'-2px',
          }}>{label}</button>
        ))}
      </div>

      {/* Reports tab */}
      {activeTab === 'reports' && (
        <div style={{ padding:'16px' }}>
          <p style={{ fontSize:'12px', color:'#9ca3af', margin:'0 0 14px' }}>
            Reports shared with you{isBoss ? ' — you can forward these to your managers' : ' — click to expand'}
          </p>
          {!hasReports
            ? <p style={{ color:'#9ca3af', fontSize:'13px', textAlign:'center', padding:'24px' }}>No reports shared yet.</p>
            : reports.map(r => {
              const isOpen    = !!expanded[r.id];
              const data      = reportData[r.id];
              const fj        = r.filter_json || {};
              const dimLabel  = DIMENSIONS.find(d => d.value === r.x_field)?.label  || r.x_field;
              const measLabel = MEASURES.find(m => m.value === r.y_field)?.label || r.y_field;
              return (
                <div key={r.id} style={{ ...st.card, marginBottom:'10px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div onClick={() => toggleReport(r)} style={{ flex:1, cursor:'pointer' }}>
                      <p style={{ fontSize:'14px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>
                        {r.report_name}
                        {r.is_global && (
                          <span style={{ marginLeft:'8px', fontSize:'10px', background:'rgba(37,99,235,0.1)', color:'#2563eb', padding:'2px 7px', borderRadius:'10px', fontWeight:'600', verticalAlign:'middle' }}>
                            🌐 Global
                          </span>
                        )}
                      </p>
                      <p style={{ fontSize:'11px', color:'#9ca3af', margin:'2px 0 0' }}>
                        {dimLabel} · {measLabel} · {r.chart_type}
                        {fj.date_from && ` · ${fj.date_from}`}{fj.date_to && ` → ${fj.date_to}`}
                      </p>
                    </div>
                    <div style={{ display:'flex', gap:'6px', alignItems:'center', marginLeft:'10px' }}>
                      {isBoss && (
                        <button onClick={() => openShare('report', r)}
                          style={{ padding:'5px 10px', background: r.shared_with_users?.length > 0 ? '#ede9fe' : '#f3f4f6',
                            border:`1px solid ${r.shared_with_users?.length > 0 ? '#7c3aed' : '#e5e7eb'}`,
                            borderRadius:'6px', fontSize:'11px', fontWeight:'600', cursor:'pointer',
                            color: r.shared_with_users?.length > 0 ? '#7c3aed' : '#6b7280' }}>
                          🔗 {r.shared_with_users?.length > 0 ? `Shared (${r.shared_with_users.length})` : 'Share'}
                        </button>
                      )}
                      <span onClick={() => toggleReport(r)} style={{ fontSize:'12px', color:'#9ca3af', cursor:'pointer' }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ marginTop:'14px', borderTop:'1px solid #f3f4f6', paddingTop:'14px' }}>
                      {loadingId === r.id
                        ? <p style={{ textAlign:'center', color:'#9ca3af', fontSize:'13px', padding:'24px 0' }}>Loading...</p>
                        : data
                          ? r.chart_type === 'table'
                            ? renderSharedTable(data, r.y_field)
                            : <ReactECharts option={getChartOpt(data, r.x_field, r.y_field, r.chart_type)} style={{ height: data.type === 'series' ? '280px' : '240px' }} />
                          : <p style={{ textAlign:'center', color:'#dc2626', fontSize:'13px', padding:'16px 0' }}>Could not load report data.</p>
                      }
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      )}

      {/* SQL Queries tab */}
      {activeTab === 'queries' && (
        <div style={{ padding:'16px' }}>
          <p style={{ fontSize:'12px', color:'#9ca3af', margin:'0 0 14px' }}>
            Queries shared with you{isBoss ? ' — you can forward these to your managers' : ' — click Run to execute'}
          </p>
          {!hasQueries
            ? <p style={{ color:'#9ca3af', fontSize:'13px', textAlign:'center', padding:'24px' }}>No queries shared yet.</p>
            : sqlQueries.map(q => {
              const res = sqlResults[q.id];
              const err = sqlErrors[q.id];
              return (
                <div key={q.id} style={{ ...st.card, marginBottom:'10px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                    <p style={{ fontSize:'14px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>{q.query_name}</p>
                    <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                      {isBoss && (
                        <button onClick={() => openShare('query', q)}
                          style={{ padding:'5px 10px', background: q.shared_with_users?.length > 0 ? '#ede9fe' : '#f3f4f6',
                            border:`1px solid ${q.shared_with_users?.length > 0 ? '#7c3aed' : '#e5e7eb'}`,
                            borderRadius:'6px', fontSize:'11px', fontWeight:'600', cursor:'pointer',
                            color: q.shared_with_users?.length > 0 ? '#7c3aed' : '#6b7280' }}>
                          🔗 {q.shared_with_users?.length > 0 ? `Shared (${q.shared_with_users.length})` : 'Share'}
                        </button>
                      )}
                      <button onClick={() => runSharedQuery(q)} disabled={runningSql === q.id}
                        style={{ padding:'7px 14px', background:'#2563eb', color:'#fff', border:'none', borderRadius:'8px',
                          fontSize:'12px', fontWeight:'600', cursor:'pointer', opacity: runningSql === q.id ? 0.6 : 1 }}>
                        {runningSql === q.id ? 'Running...' : '▶ Run'}
                      </button>
                    </div>
                  </div>
                  <pre style={{ margin:'0 0 10px', padding:'10px 12px', background:'#f8fafc', border:'1px solid #e2e8f0',
                    borderRadius:'8px', fontSize:'11px', fontFamily:'monospace', color:'#475569',
                    whiteSpace:'pre-wrap', wordBreak:'break-all', overflowX:'auto', maxHeight:'120px',
                    overflowY:'auto', userSelect:'text' }}>
                    {q.sql_query}
                  </pre>
                  {err && <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:'8px', padding:'10px 12px', color:'#dc2626', fontSize:'13px' }}>{err}</div>}
                  {res && <SqlResultTable result={res} />}
                </div>
              );
            })
          }
        </div>
      )}

      {/* Share modal (boss only) */}
      {shareModal && (
        <div style={st.modalOverlay} onClick={e => e.target === e.currentTarget && setShareModal(null)}>
          <div style={st.modalBox}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
              <p style={{ fontSize:'14px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>
                Share "{shareModal.item.report_name || shareModal.item.query_name}"
              </p>
              <button onClick={() => setShareModal(null)} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#9ca3af' }}>✕</button>
            </div>
            <p style={{ fontSize:'12px', color:'#6b7280', margin:'0 0 12px' }}>
              Select managers who can {shareModal.type === 'report' ? 'view this report' : 'run this query'}:
            </p>
            {shareMgrLoading
              ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>Loading...</p>
              : shareManagers.length === 0
                ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>No managers found.</p>
                : shareManagers.map(m => (
                  <div key={m.user_id} onClick={() => setShareSel(s => s.includes(m.user_id) ? s.filter(x => x !== m.user_id) : [...s, m.user_id])}
                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'8px', cursor:'pointer', marginBottom:'4px',
                      background: shareSelected.includes(m.user_id) ? '#ede9fe' : '#f9fafb',
                      border:`1px solid ${shareSelected.includes(m.user_id) ? '#7c3aed' : '#e5e7eb'}` }}>
                    <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0,
                      background: shareSelected.includes(m.user_id) ? '#7c3aed' : 'transparent',
                      border:`2px solid ${shareSelected.includes(m.user_id) ? '#7c3aed' : '#d1d5db'}`,
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {shareSelected.includes(m.user_id) && <span style={{ color:'#fff', fontSize:'9px' }}>✓</span>}
                    </div>
                    <div>
                      <p style={{ fontSize:'13px', fontWeight:'600', color:'#1f2937', margin:0 }}>{m.full_name}</p>
                      <p style={{ fontSize:'11px', color:'#9ca3af', margin:0 }}>{m.role} · @{m.username}</p>
                    </div>
                  </div>
                ))
            }
            <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
              <button onClick={() => setShareModal(null)} style={{ flex:1, padding:'10px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', cursor:'pointer', color:'#4b5563', fontWeight:'500' }}>Cancel</button>
              <button onClick={handleShareSave} disabled={shareSaving} style={{ flex:1, padding:'10px', background:'#7c3aed', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#fff', opacity: shareSaving ? 0.7 : 1 }}>
                {shareSaving ? 'Saving...' : '✓ Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// helper for manager shared table
const renderSharedTable = (reportData, measure) => {
  const mLabel = MEASURES.find(m => m.value === measure)?.label || measure;
  if (reportData.type === 'series') {
    const series = reportData.series || [];
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
          <thead><tr>
            <th style={st.th}>Label</th>
            {series.map(n => <th key={n} style={st.thR}>{n}</th>)}
            <th style={st.thR}>Total</th>
          </tr></thead>
          <tbody>{(reportData.data || []).map((row, i) => {
            const total = series.reduce((s, n) => s + (row[n] || 0), 0);
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                <td style={st.td}>{String(row.label)}</td>
                {series.map(n => <td key={n} style={st.tdR}>{fmtVal(row[n])}</td>)}
                <td style={{ ...st.tdR, fontWeight:'700' }}>{fmtVal(total)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    );
  }
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
        <thead><tr><th style={st.th}>Label</th><th style={st.thR}>{mLabel}</th></tr></thead>
        <tbody>{(reportData.data || []).map((row, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
            <td style={st.td}>{String(row.label)}</td>
            <td style={st.tdR}>{fmtVal(row.value)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

// shared chart option builder
const getChartOpt = (reportData, xField, yField, chartType) => {
  const mLabel = MEASURES.find(m => m.value === yField)?.label || yField;
  if (reportData.type === 'series') {
    const labels = (reportData.data || []).map(d => String(d.label));
    const series = reportData.series || [];
    return {
      backgroundColor:'transparent',
      tooltip:{ trigger:'axis' },
      legend:{ data:series, top:0, type:'scroll', textStyle:{ fontSize:10 } },
      grid:{ left:8, right:8, top:44, bottom:20, containLabel:true },
      xAxis:{ type:'category', data:labels, axisLabel:{ rotate: labels.length > 6 ? 30 : 0, fontSize:10, interval:0 } },
      yAxis:{ type:'value', name:mLabel, axisLabel:{ fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v } },
      series: series.map((name, i) => ({
        name, type: chartType === 'line' ? 'line' : 'bar', smooth: chartType === 'line',
        data:(reportData.data || []).map(d => d[name] || 0),
        itemStyle:{ color: CHART_COLORS[i % CHART_COLORS.length] },
      })),
    };
  }
  const labels = (reportData.data || []).map(d => String(d.label));
  const values = (reportData.data || []).map(d => parseFloat(d.value || 0));
  return {
    backgroundColor:'transparent',
    tooltip:{ trigger:'axis' },
    grid:{ left:8, right:8, top:8, bottom:20, containLabel:true },
    xAxis:{ type:'category', data:labels, axisLabel:{ rotate: labels.length > 6 ? 30 : 0, fontSize:10, interval:0 } },
    yAxis:{ type:'value', name:mLabel, axisLabel:{ fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v } },
    series:[{ type: chartType === 'line' ? 'line' : 'bar', smooth: chartType === 'line', data:values, itemStyle:{ color:'#2563eb' } }],
  };
};

// ─── SQL Tool (boss only) ─────────────────────────────────────────────────────

const SqlShareModal = ({ query, onClose, onSaved }) => {
  const [managers, setManagers]   = useState([]);
  const [selected, setSelected]   = useState(query.shared_with_users || []);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    getSharableUsers().then(r => setManagers(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleSave = async () => {
    setSaving(true);
    try { await shareSqlQuery(query.id, selected); onSaved(query.id, selected); onClose(); }
    catch { /* silent */ } finally { setSaving(false); }
  };

  return (
    <div style={st.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={st.modalBox}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
          <p style={{ fontSize:'14px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>Share "{query.query_name}"</p>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#9ca3af' }}>✕</button>
        </div>
        <p style={{ fontSize:'12px', color:'#6b7280', margin:'0 0 12px' }}>Managers can run this query but cannot see or edit the SQL:</p>
        {loading ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>Loading...</p>
          : managers.length === 0 ? <p style={{ fontSize:'12px', color:'#9ca3af', textAlign:'center', padding:'16px' }}>No managers found.</p>
          : managers.map(m => (
            <div key={m.user_id} onClick={() => toggle(m.user_id)}
              style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px', borderRadius:'8px', cursor:'pointer', marginBottom:'4px',
                background: selected.includes(m.user_id) ? '#ede9fe' : '#f9fafb',
                border: `1px solid ${selected.includes(m.user_id) ? '#7c3aed' : '#e5e7eb'}` }}>
              <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0,
                background: selected.includes(m.user_id) ? '#7c3aed' : 'transparent',
                border: `2px solid ${selected.includes(m.user_id) ? '#7c3aed' : '#d1d5db'}`,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                {selected.includes(m.user_id) && <span style={{ color:'#fff', fontSize:'9px' }}>✓</span>}
              </div>
              <div>
                <p style={{ fontSize:'13px', fontWeight:'600', color:'#1f2937', margin:0 }}>{m.full_name}</p>
                <p style={{ fontSize:'11px', color:'#9ca3af', margin:0 }}>{m.role} · @{m.username}</p>
              </div>
            </div>
          ))
        }
        <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
          <button onClick={onClose} style={{ flex:1, padding:'10px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', cursor:'pointer', color:'#4b5563', fontWeight:'500' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex:1, padding:'10px', background:'#7c3aed', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#fff', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : '✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SqlResultTable = ({ result }) => {
  if (!result) return null;
  const showing = result.display_data || [];

  return (
    <div style={st.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <div>
          <p style={{ ...st.cardTitle, marginBottom:'2px' }}>Results</p>
          <p style={{ fontSize:'11px', color:'#9ca3af', margin:0 }}>
            Showing {showing.length} of {result.total_rows} row{result.total_rows !== 1 ? 's' : ''}
            {result.capped && <span style={{ color:'#d97706' }}> · capped at 10,000</span>}
          </p>
        </div>
        <button onClick={() => downloadSqlCSV(result.columns, result.all_data)}
          style={{ padding:'6px 12px', background:'#16a34a', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
          ⬇ CSV ({result.total_rows})
        </button>
      </div>
      {result.total_rows === 0
        ? <p style={{ textAlign:'center', color:'#9ca3af', fontSize:'13px', padding:'16px 0' }}>No rows returned.</p>
        : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead><tr>{result.columns.map(c => <th key={c} style={st.th}>{c}</th>)}</tr></thead>
              <tbody>
                {showing.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                    {row.map((cell, j) => (
                      <td key={j} style={st.td}>{cell === null ? <span style={{ color:'#d1d5db' }}>null</span> : String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
};

const SqlTool = () => {
  const { user } = useAuth();
  const [sql, setSql]                   = useState('');
  const [running, setRunning]           = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState('');
  const [queryName, setQueryName]       = useState('');
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');
  const [savedQueries, setSavedQueries] = useState([]);
  const [showSaved, setShowSaved]       = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [shareModal, setShareModal]     = useState(null);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const tenantHint = `WHERE tenant_id = '${user?.tenant_id}'`;

  const run = async () => {
    if (!sql.trim()) return;
    setRunning(true); setResult(null); setError('');
    try {
      const res = await runSqlQuery(sql);
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response ? `HTTP ${err.response.status}` : err.message)
        || 'Query failed.';
      setError(msg);
    } finally { setRunning(false); }
  };

  const handleSave = async () => {
    if (!queryName.trim()) { setSaveMsg('Enter a query name.'); return; }
    if (!sql.trim())       { setSaveMsg('Write a query first.'); return; }
    setSaving(true); setSaveMsg('');
    try {
      await saveSqlQuery({ query_name: queryName, sql_query: sql });
      setSaveMsg(`"${queryName}" saved.`);
      setQueryName('');
    } catch (err) {
      setSaveMsg(err.response?.data?.error || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const loadSavedQueries = async () => {
    setLoadingSaved(true);
    try {
      const res = await getSavedSqlQueries();
      setSavedQueries(res.data.data || []);
      setShowSaved(true);
    } catch { /* silent */ } finally { setLoadingSaved(false); }
  };

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try {
      await deleteSavedSqlQuery(id);
      setSavedQueries(s => s.filter(q => q.id !== id));
    } catch { /* silent */ } finally { setDeleting(null); }
  };

  const handleShareSaved = (id, newIds) => {
    setSavedQueries(s => s.map(q => q.id === id ? { ...q, shared_with_users: newIds } : q));
  };

  const loadQuery = (q) => {
    setSql(q.sql_query);
    setQueryName(q.query_name);
    setShowSaved(false);
    setResult(null);
  };

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
        <div>
          <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>SQL Query Tool</h2>
          <p style={{ fontSize:'12px', color:'#9ca3af', marginTop:'2px', marginBottom:0 }}>
            SELECT only · Displays first 100 rows · CSV exports all · No passwords
          </p>
        </div>
        <button onClick={loadSavedQueries} disabled={loadingSaved} style={st.savedBtn}>
          {loadingSaved ? '...' : '📂 Saved'}
        </button>
      </div>

      {/* Saved queries panel */}
      {showSaved && (
        <div style={st.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
            <p style={{ fontSize:'14px', fontWeight:'600', color:'#1e3a5f', margin:0 }}>Saved Queries</p>
            <button onClick={() => setShowSaved(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'14px' }}>✕</button>
          </div>
          {savedQueries.length === 0
            ? <p style={{ color:'#9ca3af', fontSize:'13px', textAlign:'center', padding:'12px 0' }}>No saved queries yet.</p>
            : savedQueries.map(q => (
              <div key={q.id} style={{ padding:'10px 12px', borderRadius:'8px', background:'#f9fafb', marginBottom:'8px', border:'1px solid #e5e7eb' }}>
                <div onClick={() => loadQuery(q)} style={{ cursor:'pointer', marginBottom:'6px' }}>
                  <p style={{ fontSize:'13px', fontWeight:'600', color:'#1f2937', margin:0 }}>{q.query_name}</p>
                  <p style={{ fontSize:'11px', color:'#9ca3af', margin:'2px 0 0', fontFamily:'monospace',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {q.sql_query.substring(0, 60)}{q.sql_query.length > 60 ? '…' : ''}
                  </p>
                </div>
                <div style={{ display:'flex', gap:'6px' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setShareModal(q)}
                    style={{ padding:'4px 10px', background: q.shared_with_users?.length > 0 ? '#ede9fe' : 'transparent',
                      border:`1px solid ${q.shared_with_users?.length > 0 ? '#7c3aed' : '#e5e7eb'}`,
                      borderRadius:'6px', color: q.shared_with_users?.length > 0 ? '#7c3aed' : '#6b7280', fontSize:'11px', cursor:'pointer', fontWeight:'500' }}>
                    🔗 Share{q.shared_with_users?.length > 0 ? ` (${q.shared_with_users.length})` : ''}
                  </button>
                  <button onClick={(e) => handleDelete(e, q.id, q.query_name)} disabled={deleting === q.id}
                    style={{ padding:'4px 10px', background:'transparent', border:'1px solid #fca5a5', borderRadius:'6px', color:'#dc2626', fontSize:'11px', cursor:'pointer', opacity: deleting === q.id ? 0.5 : 1 }}>
                    {deleting === q.id ? '...' : '🗑'}
                  </button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* Query editor */}
      <div style={st.card}>
        <p style={{ fontSize:'11px', color:'#6b7280', margin:'0 0 6px', fontFamily:'monospace' }}>
          Hint: always add <span style={{ background:'#f3f4f6', padding:'1px 6px', borderRadius:'4px', color:'#7c3aed' }}>{tenantHint}</span>
        </p>
        <textarea
          value={sql}
          onChange={e => setSql(e.target.value)}
          placeholder={`SELECT sale_date, SUM(payment_amount) AS revenue\nFROM sales\n${tenantHint}\nGROUP BY sale_date\nORDER BY sale_date DESC`}
          style={{ width:'100%', minHeight:'120px', padding:'10px 12px', fontFamily:'monospace',
            fontSize:'12px', border:'1px solid #e5e7eb', borderRadius:'8px', outline:'none',
            resize:'vertical', boxSizing:'border-box', color:'#1f2937', background:'#f9fafb' }}
        />
        <button onClick={run} disabled={running || !sql.trim()}
          style={{ ...st.genBtn, marginTop:'10px', opacity: (running || !sql.trim()) ? 0.6 : 1 }}>
          {running ? 'Running...' : '▶  Run Query'}
        </button>

        {/* Save row */}
        <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
          <input
            style={{ flex:1, padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', outline:'none' }}
            placeholder="Query name to save..."
            value={queryName}
            onChange={e => setQueryName(e.target.value)}
          />
          <button onClick={handleSave} disabled={saving}
            style={{ ...st.saveBtn, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
        </div>
        {saveMsg && (
          <p style={{ fontSize:'12px', marginTop:'6px', color: saveMsg.includes('saved') ? '#16a34a' : '#dc2626' }}>
            {saveMsg}
          </p>
        )}
      </div>

      {error && (
        <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:'8px', padding:'12px 14px', marginBottom:'16px', color:'#dc2626', fontSize:'13px' }}>
          {error}
        </div>
      )}

      <SqlResultTable result={result} />

      {shareModal && (
        <SqlShareModal
          query={shareModal}
          onClose={() => setShareModal(null)}
          onSaved={handleShareSaved}
        />
      )}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const ReportBuilder = () => {
  const { user } = useAuth();

  const isBoss    = user?.role === 'superadmin'; // only SA gets builder
  const isManager = ['branch_manager', 'area_manager', 'boss'].includes(user?.role);

  // ── All hooks MUST come before any early return ───────────────────────────
  const [activeTab, setActiveTab]      = useState('builder');
  const [dimension,     setDimension]  = useState('staff');
  const [measure,       setMeasure]    = useState('revenue');
  const [chartType,     setChartType]  = useState('bar');
  const [datePeriod,    setDatePeriod] = useState('daily');
  const [dateFrom,      setDateFrom]   = useState('');
  const [dateTo,        setDateTo]     = useState('');
  const [colorBy,       setColorBy]    = useState('');
  const [colorByValues, setColorByVals]= useState([]);
  const [extraFilters,  setExtraFilters]=useState([]);
  const [filterOpts,    setFilterOpts] = useState({ staff:[], payment_modes:[], products:[], classify:[], age_groups:[], booking_statuses:[], customers:[] });
  const [reportData,    setReportData] = useState(null);
  const [loading,       setLoading]    = useState(false);
  const [saving,        setSaving]     = useState(false);
  const [message,       setMessage]    = useState('');
  const [msgType,       setMsgType]    = useState('ok');
  const [reportName,    setReportName] = useState('');
  const [savedReports,  setSavedReports]=useState([]);
  const [showSaved,     setShowSaved]  = useState(false);
  const [deleting,      setDeleting]   = useState(null);
  const [shareModal,    setShareModal] = useState(null);

  useEffect(() => {
    if (!isBoss) return;
    getReportFilterOptions().then(r => setFilterOpts(r.data || {})).catch(() => {});
  }, [isBoss]);

  useEffect(() => { setColorByVals([]); }, [colorBy]);
  useEffect(() => { if (colorBy === dimension) setColorBy(''); }, [dimension]);

  useEffect(() => {
  const isBooking = dimension === 'booking_status' || measure === 'bookings';
  const validDims = isBooking
    ? new Set(['booking_status', 'staff', 'customer'])
    : new Set(['staff', 'payment_mode', 'product', 'customer', 'classify_a', 'classify_b', 'age_group']);
  setExtraFilters(f => f.filter(x => !x.dimension || validDims.has(x.dimension)));
  if (colorBy && !validDims.has(colorBy)) setColorBy('');
}, [dimension, measure]);


  // ── Early returns AFTER all hooks ─────────────────────────────────────────
  if (isManager) return <ManagerView />;

  if (!isBoss) return (
    <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', height:'300px', color:'#9ca3af', textAlign:'center', gap:'8px' }}>
      <p style={{ fontSize:'48px', margin:0 }}>📊</p>
      <p>Report Builder is not available for your role.</p>
    </div>
  );

  const takenDims = new Set([dimension, ...(colorBy ? [colorBy] : []), ...extraFilters.map(f => f.dimension).filter(Boolean)]);
  const availableForColorBy  = COLOR_BY_DIMS.filter(d => d.value !== dimension && !extraFilters.some(f => f.dimension === d.value));
  const availableForNewFilter= FILTER_DIMS.filter(d => !takenDims.has(d.value));

  const optsFor = (dim) => ({
    staff:          filterOpts.staff            || [],
    payment_mode:   filterOpts.payment_modes    || [],
    product:        filterOpts.products         || [],
    customer:       filterOpts.customers        || [],
    booking_status: filterOpts.booking_statuses || [],
    classify_a:     filterOpts.classify_a       || [],
    classify_b:     filterOpts.classify_b       || [],
    age_group:      filterOpts.age_groups       || [],
  }[dim] || []);

  const addFilter    = () => { if (availableForNewFilter.length === 0) return; setExtraFilters(f => [...f, { id:uid(), dimension:'', values:[] }]); };
  const removeFilter = (id) => setExtraFilters(f => f.filter(x => x.id !== id));
  const updateFilterDim  = (id, dim) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, dimension:dim, values:[] } : x));
  const updateFilterVals = (id, vals) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, values:vals } : x));

  const showMsg = (text, type = 'ok') => { setMessage(text); setMsgType(type); };

  const buildReport = async () => {
    setLoading(true); setReportData(null); setMessage('');
    try {
      const params = { dimension, measure };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (dimension === 'date') params.period = datePeriod;
      if (colorBy) { params.color_by = colorBy; if (colorByValues.length > 0) params.color_by_values = colorByValues.join(','); }
      extraFilters.forEach(f => { if (f.dimension && f.values.length > 0) params[`ef_${f.dimension}`] = f.values.join(','); });
      const res = await apiGenerateReport(params);
      setReportData(res.data);
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to generate report.', 'err');
    } finally { setLoading(false); }
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) { showMsg('Enter a report name.', 'err'); return; }
    setSaving(true);
    try {
      await apiSaveReport({
        report_name: reportName, x_field: dimension, y_field: measure, chart_type: chartType,
        filter_json: { date_from:dateFrom, date_to:dateTo, date_period:datePeriod, color_by:colorBy, color_by_values:colorByValues, filters:extraFilters.map(f => ({ dimension:f.dimension, values:f.values })) },
      });
      showMsg(`"${reportName}" saved.`, 'ok');
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save.', 'err'); }
    finally { setSaving(false); }
  };

  const loadSavedReports = async () => {
    try {
      const res = await getSavedReports();
      setSavedReports(res.data.data || []);
      setShowSaved(true);
    } catch { showMsg('Failed to load saved reports.', 'err'); }
  };

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try {
      await deleteSavedReport(id);
      setSavedReports(s => s.filter(r => r.id !== id));
      showMsg(`"${name}" deleted.`, 'ok');
    } catch { showMsg('Failed to delete.', 'err'); }
    finally { setDeleting(null); }
  };

  const handleShareSaved = (reportId, newIds) => {
    setSavedReports(s => s.map(r => r.id === reportId ? { ...r, shared_with_users: newIds } : r));
  };

  const loadReport = (r) => {
    setDimension(r.x_field); setMeasure(r.y_field); setChartType(r.chart_type);
    const fj = r.filter_json || {};
    setDateFrom(fj.date_from || ''); setDateTo(fj.date_to || '');
    setDatePeriod(fj.date_period || 'daily'); setColorBy(fj.color_by || '');
    setColorByVals(fj.color_by_values || []);
    setExtraFilters((fj.filters || []).map(f => ({ id:uid(), dimension:f.dimension, values:f.values || [] })));
    setReportName(r.report_name); setShowSaved(false);
    showMsg(`Loaded: ${r.report_name}`, 'ok');
  };

  // ── chart option ────────────────────────────────────────────────────────────
  const getChartOption = () => {
    if (!reportData) return {};
    const mLabel = MEASURES.find(m => m.value === measure)?.label || measure;
    if (reportData.type === 'series') {
      const labels = (reportData.data || []).map(d => String(d.label));
      const series = reportData.series || [];
      return {
        backgroundColor:'transparent',
        tooltip:{ trigger:'axis', axisPointer:{ type: chartType === 'bar' ? 'shadow' : 'line' } },
        legend:{ data:series, top:0, type:'scroll', textStyle:{ fontSize:10 }, padding:[4,4,4,4] },
        grid:{ left:8, right:8, top:44, bottom:20, containLabel:true },
        xAxis:{ type:'category', data:labels, axisLabel:{ rotate: labels.length > 6 ? 30 : 0, fontSize:10, interval:0 } },
        yAxis:{ type:'value', name:mLabel, axisLabel:{ fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v } },
        series: series.map((name, i) => ({
          name, type: chartType === 'line' ? 'line' : 'bar', smooth: chartType === 'line',
          data:(reportData.data || []).map(d => d[name] || 0),
          itemStyle:{ color: CHART_COLORS[i % CHART_COLORS.length], borderRadius: chartType === 'bar' ? [3,3,0,0] : undefined },
          lineStyle:{ color: CHART_COLORS[i % CHART_COLORS.length], width:2 },
          areaStyle: chartType === 'line' ? { color: CHART_COLORS[i % CHART_COLORS.length] + '20' } : undefined,
        })),
      };
    }
    const labels = (reportData.data || []).map(d => String(d.label));
    const values = (reportData.data || []).map(d => parseFloat(d.value || 0));
    const type   = chartType === 'line' ? 'line' : 'bar';
    return {
      backgroundColor:'transparent',
      tooltip:{ trigger:'axis', axisPointer:{ type: type === 'bar' ? 'shadow' : 'line' } },
      grid:{ left:8, right:8, top:8, bottom:20, containLabel:true },
      xAxis:{ type:'category', data:labels, axisLabel:{ rotate: labels.length > 6 ? 30 : 0, fontSize:10, interval:0 } },
      yAxis:{ type:'value', name:mLabel, axisLabel:{ fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v } },
      series:[{ type, smooth: type === 'line', data:values,
        itemStyle:{ color:'#2563eb', borderRadius: type === 'bar' ? [4,4,0,0] : undefined },
        lineStyle:{ color:'#2563eb', width:2 },
        areaStyle: type === 'line' ? { color:'rgba(37,99,235,0.1)' } : undefined }],
    };
  };

  // ── table renderer ──────────────────────────────────────────────────────────
  const renderTable = () => {
    if (!reportData) return null;
    const mLabel = MEASURES.find(m => m.value === measure)?.label || measure;
    if (reportData.type === 'series') {
      const series = reportData.series || [];
      return (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead><tr>
              <th style={st.th}>Label</th>
              {series.map(n => <th key={n} style={st.thR}>{n}</th>)}
              <th style={st.thR}>Total</th>
            </tr></thead>
            <tbody>{(reportData.data || []).map((row, i) => {
              const total = series.reduce((s, n) => s + (row[n] || 0), 0);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                  <td style={st.td}>{String(row.label)}</td>
                  {series.map(n => <td key={n} style={st.tdR}>{fmtVal(row[n])}</td>)}
                  <td style={{ ...st.tdR, fontWeight:'700', color:'#1e3a5f' }}>{fmtVal(total)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      );
    }
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
          <thead><tr><th style={st.th}>Label</th><th style={st.thR}>{mLabel}</th></tr></thead>
          <tbody>{(reportData.data || []).map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
              <td style={st.td}>{String(row.label)}</td>
              <td style={st.tdR}>{fmtVal(row.value)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  };

  const dimLabel  = DIMENSIONS.find(d => d.value === dimension)?.label || dimension;
  const measLabel = MEASURES.find(m => m.value === measure)?.label || measure;

  return (
    <div style={{ paddingBottom:'32px' }}>

      {/* Tab switcher: Builder | SQL */}
      <div style={{ display:'flex', gap:'0', borderBottom:'2px solid #e5e7eb', marginBottom:'0' }}>
        {[['builder','📊 Report Builder'],['sql','🛢 SQL Tool']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex:1, padding:'12px 8px', border:'none', background:'none', cursor:'pointer',
            fontSize:'13px', fontWeight:'600',
            color: activeTab === tab ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom:'-2px',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* SQL Tab */}
      {activeTab === 'sql' && <SqlTool />}

      {/* Builder Tab */}
      {activeTab === 'builder' && (
        <div style={{ padding:'16px' }}>

          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
            <div>
              <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#1e3a5f', margin:0 }}>Report Builder</h2>
              <p style={{ fontSize:'12px', color:'#9ca3af', marginTop:'2px', marginBottom:0 }}>Build custom reports from your data</p>
            </div>
            <button onClick={loadSavedReports} style={st.savedBtn}>📂 Saved</button>
          </div>

          {/* Message */}
          {message && (
            <div style={{ ...st.msg, background: msgType === 'err' ? '#fee2e2' : '#dcfce7', color: msgType === 'err' ? '#dc2626' : '#16a34a' }}>
              {message}
              <button onClick={() => setMessage('')} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'14px' }}>✕</button>
            </div>
          )}

          {/* Saved panel */}
          {showSaved && (
            <div style={st.card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                <p style={{ fontSize:'14px', fontWeight:'600', color:'#1e3a5f', margin:0 }}>Saved Reports</p>
                <button onClick={() => setShowSaved(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'14px' }}>✕</button>
              </div>
              {savedReports.length === 0
                ? <p style={{ color:'#9ca3af', fontSize:'13px', textAlign:'center', padding:'16px' }}>No saved reports yet.</p>
                : savedReports.map((r) => (
                  <div key={r.id} onClick={() => loadReport(r)}
                    style={{ padding:'10px 12px', borderRadius:'8px', cursor:'pointer', background:'#f9fafb', marginBottom:'8px', border:'1px solid #e5e7eb' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:'13px', fontWeight:'600', color:'#1f2937', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.report_name}</p>
                        <p style={{ fontSize:'11px', color:'#9ca3af', marginTop:'2px', marginBottom:'6px' }}>{r.x_field} · {r.y_field} · {r.chart_type}</p>
                      </div>
                    </div>
                    {/* Action buttons row */}
                    <div style={{ display:'flex', gap:'6px' }} onClick={e => e.stopPropagation()}>
                      {/* Share button */}
                      <button onClick={(e) => { e.stopPropagation(); setShareModal(r); }}
                        style={{ padding:'4px 10px', background: (r.shared_with_users?.length > 0) ? '#ede9fe' : 'transparent',
                          border:`1px solid ${(r.shared_with_users?.length > 0) ? '#7c3aed' : '#e5e7eb'}`,
                          borderRadius:'6px', color: (r.shared_with_users?.length > 0) ? '#7c3aed' : '#6b7280', fontSize:'11px', cursor:'pointer', fontWeight:'500' }}>
                        🔗 Share{r.shared_with_users?.length > 0 ? ` (${r.shared_with_users.length})` : ''}
                      </button>
                      {/* Delete button */}
                      <button onClick={(e) => handleDelete(e, r.id, r.report_name)} disabled={deleting === r.id}
                        style={{ padding:'4px 10px', background:'transparent', border:'1px solid #fca5a5', borderRadius:'6px', color:'#dc2626', fontSize:'11px', cursor:'pointer', opacity: deleting === r.id ? 0.5 : 1 }}>
                        {deleting === r.id ? '...' : '🗑 Delete'}
                      </button>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {/* Config card */}
          <div style={st.card}>
            <p style={st.cardTitle}>Configure Report</p>

            <div style={st.section}>
              <p style={st.sLabel}>X Axis (Group By)</p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {DIMENSIONS.map(d => <button key={d.value} onClick={() => setDimension(d.value)} style={st.optBtn(dimension === d.value, '#2563eb')}>{d.label}</button>)}
              </div>
              {dimension === 'date' && (
                <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                  {DATE_PERIODS.map(p => <button key={p.value} onClick={() => setDatePeriod(p.value)} style={st.optBtn(datePeriod === p.value, '#0891b2')}>{p.label}</button>)}
                </div>
              )}
            </div>

            <div style={st.section}>
              <p style={st.sLabel}>Y Axis (Measure)</p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {MEASURES.map(m => <button key={m.value} onClick={() => setMeasure(m.value)} style={st.optBtn(measure === m.value, '#16a34a')}>{m.label}</button>)}
              </div>
            </div>

            <div style={st.section}>
              <p style={st.sLabel}>Chart Type</p>
              <div style={{ display:'flex', gap:'8px' }}>
                {CHART_TYPES.map(c => (
                  <button key={c.value} onClick={() => setChartType(c.value)} style={st.chartBtn(chartType === c.value)}>
                    <span style={{ fontSize:'20px' }}>{c.icon}</span><span>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={st.section}>
              <p style={st.sLabel}>Color By <span style={{ fontWeight:'400', color:'#9ca3af' }}>(Optional)</span></p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom: colorBy ? '8px' : 0 }}>
                <button onClick={() => { setColorBy(''); setColorByVals([]); }} style={st.optBtn(!colorBy, '#6b7280')}>None</button>
                {availableForColorBy.map(d => <button key={d.value} onClick={() => setColorBy(d.value)} style={st.optBtn(colorBy === d.value, '#7c3aed')}>{d.label}</button>)}
              </div>
              {colorBy && (
                <div>
                  <p style={{ fontSize:'11px', color:'#6b7280', margin:'0 0 4px' }}>Select {COLOR_BY_DIMS.find(d => d.value === colorBy)?.label} values:</p>
                  <MultiSelect label={`${COLOR_BY_DIMS.find(d => d.value === colorBy)?.label} values`} options={optsFor(colorBy)} selected={colorByValues} onChange={setColorByVals} />
                  {colorByValues.length === 0 && <p style={{ fontSize:'10px', color:'#d97706', marginTop:'4px' }}>No selection = all values included</p>}
                </div>
              )}
            </div>

            <div style={st.section}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <p style={{ ...st.sLabel, margin:0 }}>Filters <span style={{ fontWeight:'400', color:'#9ca3af' }}>(Optional)</span></p>
                {availableForNewFilter.length > 0 && (
                  <button onClick={addFilter} style={{ padding:'4px 12px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'20px', fontSize:'11px', color:'#4b5563', cursor:'pointer', fontWeight:'500' }}>+ Add Filter</button>
                )}
              </div>
              {extraFilters.length === 0 && <p style={{ fontSize:'11px', color:'#9ca3af' }}>No filters added.</p>}
              {extraFilters.map((f, idx) => {
                const availDimsForThis = FILTER_DIMS.filter(d => d.value === f.dimension || !takenDims.has(d.value) || (takenDims.has(d.value) && d.value === f.dimension));
                return (
                  <div key={f.id} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'10px', padding:'10px 12px', marginBottom:'8px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                      <p style={{ fontSize:'11px', fontWeight:'600', color:'#4b5563', margin:0 }}>Filter {idx + 1}</p>
                      <button onClick={() => removeFilter(f.id)} style={{ background:'none', border:'none', color:'#9ca3af', fontSize:'16px', cursor:'pointer', lineHeight:1, padding:'0 2px' }}>✕</button>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:'8px' }}>
                      {availDimsForThis.map(d => <button key={d.value} onClick={() => updateFilterDim(f.id, d.value)} style={st.optBtn(f.dimension === d.value, '#0891b2')}>{d.label}</button>)}
                    </div>
                    {f.dimension && (
                      <MultiSelect label={`Select ${FILTER_DIMS.find(d => d.value === f.dimension)?.label || f.dimension} values`} options={optsFor(f.dimension)} selected={f.values} onChange={(v) => updateFilterVals(f.id, v)} />
                    )}
                    {f.dimension && f.values.length === 0 && <p style={{ fontSize:'10px', color:'#d97706', marginTop:'4px' }}>No selection = filter has no effect</p>}
                  </div>
                );
              })}
            </div>

            <div style={st.section}>
              <p style={st.sLabel}>Date Range <span style={{ fontWeight:'400', color:'#9ca3af' }}>(Optional)</span></p>
              <div style={{ display:'flex', gap:'10px' }}>
                {[['From', dateFrom, setDateFrom], ['To', dateTo, setDateTo]].map(([label, val, setter]) => (
                  <div key={label} style={{ flex:1 }}>
                    <label style={{ display:'block', fontSize:'11px', color:'#6b7280', marginBottom:'4px' }}>{label}</label>
                    <input type="date" value={val} onChange={e => setter(e.target.value)}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'12px', outline:'none', boxSizing:'border-box' }} />
                  </div>
                ))}
              </div>
            </div>

            <button onClick={buildReport} disabled={loading} style={{ ...st.genBtn, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Generating...' : '▶  Generate Report'}
            </button>
          </div>

          {/* Output */}
          {reportData && (
            <div style={st.card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                <div>
                  <p style={{ ...st.cardTitle, marginBottom:'2px' }}>{measLabel} by {dimLabel}</p>
                  <p style={{ fontSize:'11px', color:'#9ca3af', margin:0 }}>{reportData.data?.length || 0} data points</p>
                </div>
                {chartType === 'table' && (
                  <button onClick={() => downloadCSV(reportData, dimension, measure)}
                    style={{ padding:'6px 12px', background:'#16a34a', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                    ⬇ CSV
                  </button>
                )}
              </div>
              {chartType === 'table' ? renderTable() : <ReactECharts option={getChartOption()} style={{ height: reportData.type === 'series' ? '300px' : '260px' }} />}
              <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
                <input style={{ flex:1, padding:'10px 12px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', outline:'none' }}
                  placeholder="Report name to save..." value={reportName} onChange={e => setReportName(e.target.value)} />
                <button onClick={handleSaveReport} disabled={saving} style={{ ...st.saveBtn, opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : '💾 Save'}
                </button>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Share Modal */}
      {shareModal && (
        <ShareModal
          report={shareModal}
          onClose={() => setShareModal(null)}
          onSaved={handleShareSaved}
        />
      )}

    </div>
  );
};

// ─── styles ───────────────────────────────────────────────────────────────────

const st = {
  card:     { background:'#fff', borderRadius:'12px', padding:'16px', marginBottom:'16px', boxShadow:'0 1px 3px rgba(0,0,0,0.1)' },
  cardTitle:{ fontSize:'14px', fontWeight:'600', color:'#1e3a5f', margin:'0 0 12px' },
  section:  { marginBottom:'16px' },
  sLabel:   { fontSize:'12px', fontWeight:'600', color:'#4b5563', margin:'0 0 8px' },
  savedBtn: { padding:'8px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'12px', cursor:'pointer', color:'#4b5563', fontWeight:'500' },
  msg:      { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderRadius:'8px', marginBottom:'16px', fontSize:'13px' },
  genBtn:   { width:'100%', padding:'13px', background:'#2563eb', color:'#fff', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer' },
  saveBtn:  { padding:'10px 16px', background:'#16a34a', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer' },
  th:       { padding:'8px 10px', textAlign:'left',  fontSize:'11px', fontWeight:'600', color:'#6b7280', borderBottom:'2px solid #e5e7eb', whiteSpace:'nowrap', background:'#f9fafb' },
  thR:      { padding:'8px 10px', textAlign:'right', fontSize:'11px', fontWeight:'600', color:'#6b7280', borderBottom:'2px solid #e5e7eb', whiteSpace:'nowrap', background:'#f9fafb' },
  td:       { padding:'7px 10px', color:'#1f2937', fontSize:'12px', borderBottom:'1px solid #f3f4f6' },
  tdR:      { padding:'7px 10px', textAlign:'right', color:'#374151', fontSize:'12px', borderBottom:'1px solid #f3f4f6', fontVariantNumeric:'tabular-nums' },

  optBtn:  (active, color) => ({ padding:'6px 12px', border:`1px solid ${active ? color : '#e5e7eb'}`, borderRadius:'20px', fontSize:'11px', cursor:'pointer', fontWeight:'500', transition:'all 0.15s', background: active ? color : '#f3f4f6', color: active ? '#fff' : '#4b5563' }),
  chartBtn:(active) => ({ flex:1, padding:'10px 8px', border:`1px solid ${active ? '#7c3aed' : '#e5e7eb'}`, borderRadius:'10px', fontSize:'12px', cursor:'pointer', fontWeight:'500', display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', background: active ? '#7c3aed' : '#f3f4f6', color: active ? '#fff' : '#4b5563' }),
  msBtn:   (a) => ({ width:'100%', padding:'7px 10px', background:'#fff', border:`1px solid ${a ? '#7c3aed' : '#e5e7eb'}`, borderRadius:'8px', fontSize:'12px', color: a ? '#7c3aed' : '#9ca3af', textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }),
  msDrop:  { position:'absolute', top:'100%', left:0, right:0, zIndex:400, background:'#fff', border:'1px solid #e5e7eb', borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.12)' },
  msSearch:{ width:'100%', padding:'5px 8px', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'6px', fontSize:'11px', color:'#1f2937', outline:'none', boxSizing:'border-box' },
  msAct:   (c) => ({ fontSize:'11px', color:c, background:'none', border:'none', cursor:'pointer', padding:'2px 4px', fontWeight:'600' }),
  msItem:  (a) => ({ padding:'7px 10px', background: a ? '#ede9fe' : 'transparent', color: a ? '#7c3aed' : '#1f2937', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }),
  msChk:   (a) => ({ width:'12px', height:'12px', border:`1px solid ${a ? '#7c3aed' : '#d1d5db'}`, borderRadius:'3px', background: a ? '#7c3aed' : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }),
  msApply: { fontSize:'11px', background:'#7c3aed', border:'none', borderRadius:'6px', cursor:'pointer', padding:'4px 12px', fontWeight:'600', color:'#fff' },

  modalOverlay:{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' },
  modalBox:    { background:'#fff', borderRadius:'16px', padding:'20px', width:'100%', maxWidth:'360px', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
};

export default ReportBuilder;

// ─────────────────────────────────────────────────────────────────────────────
// TenantReportBuilder — superadmin-only, PC-first, per-tenant scoped
// Props: tenant = { tenant_id, tenant_name }, onClose: () => void
// ─────────────────────────────────────────────────────────────────────────────

// Dark-theme tokens (matches SuperadminPanel C vars)
const D = {
  bg:       '#0f1117',
  surface:  '#1a1d27',
  elevated: '#22253a',
  border:   'rgba(255,255,255,0.08)',
  accent:   '#a8e063',
  accentDim:'rgba(168,224,99,0.12)',
  blue:     '#60a5fa',
  purple:   '#a78bfa',
  amber:    '#fbb724',
  danger:   '#ff6b6b',
  text:     '#e2e8f0',
  textSec:  '#94a3b8',
  textMuted:'#64748b',
};

const dBtn = (variant = 'primary') => ({
  padding: '7px 14px', borderRadius: '8px', border: 'none',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  background:
    variant === 'primary'   ? D.accent    :
    variant === 'danger'    ? 'rgba(255,107,107,0.15)' :
    variant === 'blue'      ? 'rgba(96,165,250,0.15)'  :
    variant === 'purple'    ? 'rgba(167,139,250,0.15)' :
    D.elevated,
  color:
    variant === 'primary'   ? '#000'     :
    variant === 'danger'    ? D.danger   :
    variant === 'blue'      ? D.blue     :
    variant === 'purple'    ? D.purple   :
    D.textSec,
});

// Share modal (dark themed) reused for both reports and SQL queries
const DarkShareModal = ({ title, note, items, selected, onToggle, onSave, onClose, saving, loading }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center' }}
       onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{ background:D.surface, border:`1px solid ${D.border}`, borderRadius:'16px', padding:'24px', width:'420px', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,0.6)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
        <p style={{ fontSize:'15px', fontWeight:'700', color:D.text, margin:0 }}>{title}</p>
        <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:D.textMuted }}>✕</button>
      </div>
      {note && <p style={{ fontSize:'12px', color:D.textSec, margin:'0 0 14px' }}>{note}</p>}
      {loading ? <p style={{ color:D.textMuted, fontSize:'13px', textAlign:'center', padding:'16px' }}>Loading...</p>
        : items.length === 0 ? <p style={{ color:D.textMuted, fontSize:'13px', textAlign:'center', padding:'16px' }}>No managers in this tenant.</p>
        : items.map(m => (
          <div key={m.user_id} onClick={() => onToggle(m.user_id)}
            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'8px',
              cursor:'pointer', marginBottom:'6px',
              background: selected.includes(m.user_id) ? 'rgba(167,139,250,0.12)' : D.elevated,
              border:`1px solid ${selected.includes(m.user_id) ? D.purple : D.border}` }}>
            <div style={{ width:'16px', height:'16px', borderRadius:'4px', flexShrink:0,
              background: selected.includes(m.user_id) ? D.purple : 'transparent',
              border:`2px solid ${selected.includes(m.user_id) ? D.purple : D.textMuted}`,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              {selected.includes(m.user_id) && <span style={{ color:'#fff', fontSize:'9px' }}>✓</span>}
            </div>
            <div>
              <p style={{ fontSize:'13px', fontWeight:'600', color:D.text, margin:0 }}>{m.full_name}</p>
              <p style={{ fontSize:'11px', color:D.textMuted, margin:0 }}>{m.role} · @{m.username}</p>
            </div>
          </div>
        ))
      }
      <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
        <button onClick={onClose} style={{ flex:1, padding:'10px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', fontSize:'13px', cursor:'pointer', color:D.textSec, fontWeight:'500' }}>Cancel</button>
        <button onClick={onSave} disabled={saving} style={{ flex:1, padding:'10px', background:D.purple, border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#fff', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving...' : '✓ Save'}
        </button>
      </div>
    </div>
  </div>
);

// ── Builder Tab (visual report builder) ───────────────────────────────────────
const TenantBuilderTab = ({ tenantId }) => {
  const [dimension,      setDimension]   = useState('staff');
  const [measure,        setMeasure]     = useState('revenue');
  const [chartType,      setChartType]   = useState('bar');
  const [datePeriod,     setDatePeriod]  = useState('daily');
  const [dateFrom,       setDateFrom]    = useState('');
  const [dateTo,         setDateTo]      = useState('');
  const [colorBy,        setColorBy]     = useState('');
  const [colorByValues,  setColorByVals] = useState([]);
  const [extraFilters,   setExtraFilters]= useState([]);
  const [filterOpts,     setFilterOpts]  = useState({ staff:[], payment_modes:[], products:[], classify:[], age_groups:[], booking_statuses:[], customers:[] });
  const [reportData,     setReportData]  = useState(null);
  const [loading,        setLoading]     = useState(false);
  const [saving,         setSaving]      = useState(false);
  const [message,        setMessage]     = useState('');
  const [msgType,        setMsgType]     = useState('ok');
  const [reportName,     setReportName]  = useState('');
  const [savedReports,   setSavedReports]= useState([]);
  const [showSaved,      setShowSaved]   = useState(false);
  const [deleting,       setDeleting]    = useState(null);
  const [shareModal,     setShareModal]  = useState(null);
  const [shareManagers,  setShareMgrs]   = useState([]);
  const [shareSelected,  setShareSel]    = useState([]);
  const [shareSaving,    setShareSaving] = useState(false);
  const [shareMgrLoading,setShareMgrL]   = useState(false);

  useEffect(() => {
    getReportFilterOptions(tenantId)
      .then(r => setFilterOpts(r.data || {}))
      .catch(() => {});
  }, [tenantId]);

  useEffect(() => { setColorByVals([]); }, [colorBy]);
  useEffect(() => { if (colorBy === dimension) setColorBy(''); }, [dimension]);
  useEffect(() => {
    const isBooking = dimension === 'booking_status' || measure === 'bookings';
    const validDims = isBooking
      ? new Set(['booking_status', 'staff', 'customer'])
      : new Set(['staff', 'payment_mode', 'product', 'customer', 'classify_a', 'classify_b', 'age_group']);
    setExtraFilters(f => f.filter(x => !x.dimension || validDims.has(x.dimension)));
    if (colorBy && !validDims.has(colorBy)) setColorBy('');
  }, [dimension, measure]);

  const takenDims            = new Set([dimension, ...(colorBy ? [colorBy] : []), ...extraFilters.map(f => f.dimension).filter(Boolean)]);
  const availableForColorBy  = COLOR_BY_DIMS.filter(d => d.value !== dimension && !extraFilters.some(f => f.dimension === d.value));
  const availableForNewFilter= FILTER_DIMS.filter(d => !takenDims.has(d.value));

  const optsFor = (dim) => ({
    staff:          filterOpts.staff            || [],
    payment_mode:   filterOpts.payment_modes    || [],
    product:        filterOpts.products         || [],
    customer:       filterOpts.customers        || [],
    booking_status: filterOpts.booking_statuses || [],
    classify_a:     filterOpts.classify_a       || [],
    classify_b:     filterOpts.classify_b       || [],
    age_group:      filterOpts.age_groups       || [],
  }[dim] || []);

  const addFilter        = () => { if (availableForNewFilter.length === 0) return; setExtraFilters(f => [...f, { id:uid(), dimension:'', values:[] }]); };
  const removeFilter     = (id) => setExtraFilters(f => f.filter(x => x.id !== id));
  const updateFilterDim  = (id, dim) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, dimension:dim, values:[] } : x));
  const updateFilterVals = (id, vals) => setExtraFilters(f => f.map(x => x.id === id ? { ...x, values:vals } : x));
  const showMsg          = (text, type = 'ok') => { setMessage(text); setMsgType(type); };

  const buildReport = async () => {
    setLoading(true); setReportData(null); setMessage('');
    try {
      const params = { dimension, measure, sa_tenant: tenantId };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (dimension === 'date') params.period = datePeriod;
      if (colorBy) { params.color_by = colorBy; if (colorByValues.length > 0) params.color_by_values = colorByValues.join(','); }
      extraFilters.forEach(f => { if (f.dimension && f.values.length > 0) params[`ef_${f.dimension}`] = f.values.join(','); });
      const res = await apiGenerateReport(params);
      setReportData(res.data);
    } catch (err) {
      showMsg(err.response?.data?.error || 'Failed to generate report.', 'err');
    } finally { setLoading(false); }
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) { showMsg('Enter a report name.', 'err'); return; }
    setSaving(true);
    try {
      await apiSaveReport({
        report_name: reportName, x_field: dimension, y_field: measure, chart_type: chartType,
        filter_json: { date_from:dateFrom, date_to:dateTo, date_period:datePeriod, color_by:colorBy, color_by_values:colorByValues, filters:extraFilters.map(f => ({ dimension:f.dimension, values:f.values })) },
        sa_tenant: tenantId,
      });
      showMsg(`"${reportName}" saved.`, 'ok');
      setReportName('');
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save.', 'err'); }
    finally { setSaving(false); }
  };

  const loadSavedReports = async () => {
    try { const res = await getSavedReports(tenantId); setSavedReports(res.data.data || []); setShowSaved(true); }
    catch { showMsg('Failed to load saved reports.', 'err'); }
  };

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try { await deleteSavedReport(id, tenantId); setSavedReports(s => s.filter(r => r.id !== id)); }
    catch { /* silent */ } finally { setDeleting(null); }
  };

  const openShareModal = async (report) => {
    setShareModal(report);
    setShareSel(report.shared_with_users || []);
    setShareMgrL(true);
    try { const r = await getSharableUsers(tenantId); setShareMgrs(r.data.data || []); }
    catch { /* silent */ } finally { setShareMgrL(false); }
  };

  const handleShareSave = async () => {
    setShareSaving(true);
    try {
      await shareReport(shareModal.id, shareSelected, tenantId);
      setSavedReports(s => s.map(r => r.id === shareModal.id ? { ...r, shared_with_users: shareSelected } : r));
      setShareModal(null);
    } catch { /* silent */ } finally { setShareSaving(false); }
  };

  const loadSavedReport = (r) => {
    setDimension(r.x_field || 'staff');
    setMeasure(r.y_field || 'revenue');
    setChartType(r.chart_type || 'bar');
    const fj = r.filter_json || {};
    setDateFrom(fj.date_from || ''); setDateTo(fj.date_to || '');
    setDatePeriod(fj.date_period || 'daily');
    setColorBy(fj.color_by || ''); setColorByVals(fj.color_by_values || []);
    setExtraFilters((fj.filters || []).map(f => ({ id:uid(), dimension:f.dimension, values:f.values || [] })));
    setShowSaved(false); setReportData(null);
  };

  const dimLabel  = DIMENSIONS.find(d => d.value === dimension)?.label  || dimension;
  const measLabel = MEASURES.find(m => m.value === measure)?.label      || measure;

  const isBookingView = dimension === 'booking_status' || measure === 'bookings';
  const validDims     = isBookingView
    ? DIMENSIONS.filter(d => ['booking_status','staff','customer','date'].includes(d.value))
    : DIMENSIONS.filter(d => !['booking_status'].includes(d.value));

  const getChartOption = () => {
    if (!reportData) return {};
    if (reportData.type === 'series') {
      const labels = (reportData.data||[]).map(d => String(d.label));
      const series = reportData.series||[];
      return {
        backgroundColor:'transparent', tooltip:{trigger:'axis'},
        legend:{data:series,top:0,type:'scroll',textStyle:{fontSize:11,color:D.textSec}},
        grid:{left:8,right:8,top:44,bottom:20,containLabel:true},
        xAxis:{type:'category',data:labels,axisLabel:{rotate:labels.length>6?30:0,fontSize:11,color:D.textMuted}},
        yAxis:{type:'value',axisLabel:{fontSize:11,color:D.textMuted,formatter:v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}},
        series:series.map((name,i)=>({name,type:chartType==='line'?'line':'bar',smooth:chartType==='line',data:(reportData.data||[]).map(d=>d[name]||0),itemStyle:{color:CHART_COLORS[i%CHART_COLORS.length]}})),
      };
    }
    const labels = (reportData.data||[]).map(d=>String(d.label));
    const values = (reportData.data||[]).map(d=>parseFloat(d.value||0));
    return {
      backgroundColor:'transparent', tooltip:{trigger:'axis'},
      grid:{left:8,right:8,top:8,bottom:20,containLabel:true},
      xAxis:{type:'category',data:labels,axisLabel:{rotate:labels.length>6?30:0,fontSize:11,color:D.textMuted}},
      yAxis:{type:'value',axisLabel:{fontSize:11,color:D.textMuted,formatter:v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}},
      series:[{type:chartType==='line'?'line':'bar',smooth:chartType==='line',data:values,itemStyle:{color:D.accent}}],
    };
  };

  const renderTable = () => {
    if (!reportData) return null;
    if (reportData.type === 'series') {
      const series = reportData.series||[];
      return (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
            <thead><tr>
              <th style={{padding:'8px 12px',textAlign:'left',color:D.textMuted,borderBottom:`1px solid ${D.border}`,background:D.elevated}}>Label</th>
              {series.map(n=><th key={n} style={{padding:'8px 12px',textAlign:'right',color:D.textMuted,borderBottom:`1px solid ${D.border}`,background:D.elevated}}>{n}</th>)}
              <th style={{padding:'8px 12px',textAlign:'right',color:D.textMuted,borderBottom:`1px solid ${D.border}`,background:D.elevated}}>Total</th>
            </tr></thead>
            <tbody>{(reportData.data||[]).map((row,i)=>{
              const total = series.reduce((s,n)=>s+(row[n]||0),0);
              return <tr key={i} style={{background:i%2===0?'transparent':D.elevated}}>
                <td style={{padding:'8px 12px',color:D.text}}>{String(row.label)}</td>
                {series.map(n=><td key={n} style={{padding:'8px 12px',textAlign:'right',color:D.textSec}}>{fmtVal(row[n])}</td>)}
                <td style={{padding:'8px 12px',textAlign:'right',color:D.text,fontWeight:'700'}}>{fmtVal(total)}</td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      );
    }
    return (
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
          <thead><tr>
            <th style={{padding:'8px 12px',textAlign:'left',color:D.textMuted,borderBottom:`1px solid ${D.border}`,background:D.elevated}}>Label</th>
            <th style={{padding:'8px 12px',textAlign:'right',color:D.textMuted,borderBottom:`1px solid ${D.border}`,background:D.elevated}}>{measLabel}</th>
          </tr></thead>
          <tbody>{(reportData.data||[]).map((row,i)=>(
            <tr key={i} style={{background:i%2===0?'transparent':D.elevated}}>
              <td style={{padding:'8px 12px',color:D.text}}>{String(row.label)}</td>
              <td style={{padding:'8px 12px',textAlign:'right',color:D.textSec}}>{fmtVal(row.value)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  };

  const optBtn = (active, color) => ({
    padding:'5px 11px', border:`1px solid ${active ? color : D.border}`,
    borderRadius:'20px', fontSize:'12px', cursor:'pointer', fontWeight:'500',
    background: active ? color : D.elevated, color: active ? '#000' : D.textSec,
  });

  return (
    <div style={{ display:'flex', gap:'0', height:'100%', overflow:'hidden' }}>

      {/* ── Left Config Panel ─────────────────────────────── */}
      <div style={{ width:'360px', flexShrink:0, borderRight:`1px solid ${D.border}`, overflowY:'auto', padding:'20px' }}>

        {/* Saved reports toggle */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <p style={{ fontSize:'13px', fontWeight:'700', color:D.text, margin:0 }}>Configure Report</p>
          <button onClick={loadSavedReports} style={dBtn('secondary')}>📂 Saved</button>
        </div>

        {/* Saved panel */}
        {showSaved && (
          <div style={{ background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'10px', padding:'14px', marginBottom:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <p style={{ fontSize:'13px', fontWeight:'600', color:D.text, margin:0 }}>Saved Reports</p>
              <button onClick={() => setShowSaved(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'14px', color:D.textMuted }}>✕</button>
            </div>
            {savedReports.length === 0
              ? <p style={{ color:D.textMuted, fontSize:'12px', textAlign:'center', padding:'8px 0' }}>No saved reports yet.</p>
              : savedReports.map(r => (
                <div key={r.id} style={{ padding:'9px 10px', borderRadius:'8px', background:D.surface, marginBottom:'6px', border:`1px solid ${D.border}` }}>
                  <div onClick={() => loadSavedReport(r)} style={{ cursor:'pointer', marginBottom:'6px' }}>
                    <p style={{ fontSize:'13px', fontWeight:'600', color:D.text, margin:0 }}>{r.report_name}</p>
                    <p style={{ fontSize:'11px', color:D.textMuted, margin:'2px 0 0' }}>{r.x_field} · {r.y_field}</p>
                  </div>
                  <div style={{ display:'flex', gap:'6px' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => openShareModal(r)}
                      style={{ ...dBtn(r.shared_with_users?.length > 0 ? 'purple' : 'secondary'), fontSize:'11px', padding:'3px 9px' }}>
                      🔗 {r.shared_with_users?.length > 0 ? `Shared (${r.shared_with_users.length})` : 'Share'}
                    </button>
                    <button onClick={(e) => handleDelete(e, r.id, r.report_name)} disabled={deleting===r.id}
                      style={{ padding:'3px 9px', background:'transparent', border:`1px solid ${D.danger}`, borderRadius:'6px', color:D.danger, fontSize:'11px', cursor:'pointer', opacity:deleting===r.id?0.5:1 }}>
                      {deleting===r.id ? '...' : '🗑'}
                    </button>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* Dimension */}
        <div style={{ marginBottom:'16px' }}>
          <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Dimension (X-axis)</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
            {validDims.map(d => <button key={d.value} onClick={() => setDimension(d.value)} style={optBtn(dimension===d.value, D.accent)}>{d.label}</button>)}
          </div>
        </div>

        {/* Date period */}
        {dimension === 'date' && (
          <div style={{ marginBottom:'16px' }}>
            <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Period</p>
            <div style={{ display:'flex', gap:'6px' }}>
              {DATE_PERIODS.map(p => <button key={p.value} onClick={() => setDatePeriod(p.value)} style={optBtn(datePeriod===p.value, D.accent)}>{p.label}</button>)}
            </div>
          </div>
        )}

        {/* Measure */}
        <div style={{ marginBottom:'16px' }}>
          <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Measure (Y-axis)</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
            {MEASURES.map(m => <button key={m.value} onClick={() => setMeasure(m.value)} style={optBtn(measure===m.value, D.accent)}>{m.label}</button>)}
          </div>
        </div>

        {/* Chart type */}
        <div style={{ marginBottom:'16px' }}>
          <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Chart Type</p>
          <div style={{ display:'flex', gap:'6px' }}>
            {CHART_TYPES.map(c => (
              <button key={c.value} onClick={() => setChartType(c.value)}
                style={{ flex:1, padding:'8px 4px', border:`1px solid ${chartType===c.value?D.purple:D.border}`, borderRadius:'8px', fontSize:'12px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'3px', background:chartType===c.value?'rgba(167,139,250,0.15)':D.elevated, color:chartType===c.value?D.purple:D.textSec }}>
                <span>{c.icon}</span><span>{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Color by */}
        {dimension !== 'date' && (
          <div style={{ marginBottom:'16px' }}>
            <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Color By <span style={{ fontWeight:'400' }}>(Optional)</span></p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'6px' }}>
              {colorBy && <button onClick={() => setColorBy('')} style={optBtn(false, D.border)}>✕ Clear</button>}
              {availableForColorBy.map(d => <button key={d.value} onClick={() => setColorBy(d.value)} style={optBtn(colorBy===d.value, D.purple)}>{d.label}</button>)}
            </div>
            {colorBy && (
              <MultiSelect label={`${COLOR_BY_DIMS.find(d=>d.value===colorBy)?.label} values`}
                options={optsFor(colorBy)} selected={colorByValues} onChange={setColorByVals} />
            )}
          </div>
        )}

        {/* Filters */}
        <div style={{ marginBottom:'16px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
            <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:0, textTransform:'uppercase', letterSpacing:'0.5px' }}>Filters <span style={{ fontWeight:'400' }}>(Optional)</span></p>
            {availableForNewFilter.length > 0 && (
              <button onClick={addFilter} style={{ padding:'3px 10px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'20px', fontSize:'11px', color:D.textSec, cursor:'pointer' }}>+ Add</button>
            )}
          </div>
          {extraFilters.length === 0 && <p style={{ fontSize:'11px', color:D.textMuted }}>No filters.</p>}
          {extraFilters.map((f, idx) => {
            const availDims = FILTER_DIMS.filter(d => d.value===f.dimension || !takenDims.has(d.value));
            return (
              <div key={f.id} style={{ background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', padding:'10px', marginBottom:'8px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                  <p style={{ fontSize:'11px', fontWeight:'600', color:D.textSec, margin:0 }}>Filter {idx+1}</p>
                  <button onClick={() => removeFilter(f.id)} style={{ background:'none', border:'none', color:D.textMuted, fontSize:'14px', cursor:'pointer' }}>✕</button>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:'6px' }}>
                  {availDims.map(d => <button key={d.value} onClick={() => updateFilterDim(f.id, d.value)} style={optBtn(f.dimension===d.value, D.blue)}>{d.label}</button>)}
                </div>
                {f.dimension && <MultiSelect label={`Select ${FILTER_DIMS.find(d=>d.value===f.dimension)?.label || f.dimension}`} options={optsFor(f.dimension)} selected={f.values} onChange={(v) => updateFilterVals(f.id, v)} />}
              </div>
            );
          })}
        </div>

        {/* Date range */}
        <div style={{ marginBottom:'20px' }}>
          <p style={{ fontSize:'11px', fontWeight:'700', color:D.textMuted, margin:'0 0 8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Date Range <span style={{ fontWeight:'400' }}>(Optional)</span></p>
          <div style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
            {[['From', dateFrom, setDateFrom], ['To', dateTo, setDateTo]].map(([label, val, setter]) => (
              <div key={label} style={{ flex:1 }}>
                <label style={{ display:'block', fontSize:'11px', color:D.textMuted, marginBottom:'4px' }}>{label}</label>
                <input type="date" value={val} onChange={e => setter(e.target.value)}
                  style={{ width:'100%', padding:'8px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', fontSize:'12px', color:D.text, outline:'none', boxSizing:'border-box', colorScheme:'dark' }} />
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'6px' }}>
            <button onClick={() => { if (reportData) buildReport(); }}
              disabled={loading || !reportData}
              style={{ flex:1, padding:'8px', background: (!reportData || loading) ? D.elevated : D.blue, color: (!reportData || loading) ? D.textMuted : '#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor: (!reportData || loading) ? 'not-allowed' : 'pointer' }}>
              {loading ? '...' : '✓ Apply Dates'}
            </button>
            <button onClick={() => { setDateFrom(''); setDateTo(''); if (reportData) setTimeout(buildReport, 0); }}
              style={{ padding:'8px 12px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', fontSize:'12px', color:D.textSec, cursor:'pointer' }}>
              Clear
            </button>
          </div>
        </div>

        <button onClick={buildReport} disabled={loading}
          style={{ width:'100%', padding:'12px', background: loading ? D.elevated : D.accent, color: loading ? D.textMuted : '#000', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Generating...' : '▶  Generate Report'}
        </button>
      </div>

      {/* ── Right Output Panel ────────────────────────────── */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>
        {message && (
          <div style={{ background: msgType==='ok' ? 'rgba(168,224,99,0.1)' : 'rgba(255,107,107,0.1)', border:`1px solid ${msgType==='ok'?D.accent:D.danger}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', color: msgType==='ok'?D.accent:D.danger, fontSize:'13px' }}>
            {message}
          </div>
        )}

        {!reportData && !loading && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'300px', color:D.textMuted, gap:'12px' }}>
            <p style={{ fontSize:'48px', margin:0 }}>📊</p>
            <p style={{ fontSize:'14px' }}>Configure your report on the left, then hit Generate.</p>
          </div>
        )}

        {reportData && (
          <div style={{ background:D.surface, border:`1px solid ${D.border}`, borderRadius:'12px', padding:'20px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
              <div>
                <p style={{ fontSize:'16px', fontWeight:'700', color:D.text, margin:0 }}>{measLabel} by {dimLabel}</p>
                <p style={{ fontSize:'12px', color:D.textMuted, margin:'3px 0 0' }}>{reportData.data?.length || 0} data points</p>
              </div>
              {chartType === 'table' && (
                <button onClick={() => downloadCSV(reportData, dimension, measure)}
                  style={{ padding:'6px 12px', background:'rgba(168,224,99,0.1)', border:`1px solid ${D.accent}`, borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer', color:D.accent }}>
                  ⬇ CSV
                </button>
              )}
            </div>

            {chartType === 'table'
              ? renderTable()
              : <ReactECharts option={getChartOption()} style={{ height: reportData.type==='series' ? '320px' : '280px' }} />
            }

            <div style={{ display:'flex', gap:'8px', marginTop:'20px', borderTop:`1px solid ${D.border}`, paddingTop:'16px' }}>
              <input
                style={{ flex:1, padding:'10px 12px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', fontSize:'13px', color:D.text, outline:'none' }}
                placeholder="Report name to save..."
                value={reportName}
                onChange={e => setReportName(e.target.value)}
              />
              <button onClick={handleSaveReport} disabled={saving}
                style={{ padding:'10px 18px', background:D.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Share modal */}
      {shareModal && (
        <DarkShareModal
          title={`Share "${shareModal.report_name}"`}
          note="Managers of this tenant who can view this report:"
          items={shareManagers}
          selected={shareSelected}
          onToggle={(id) => setShareSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])}
          onSave={handleShareSave}
          onClose={() => setShareModal(null)}
          saving={shareSaving}
          loading={shareMgrLoading}
        />
      )}
    </div>
  );
};

// ── SQL Tool Tab (tenant-scoped) ───────────────────────────────────────────────
const TenantSqlTab = ({ tenantId }) => {
  const [sql, setSql]                   = useState('');
  const [running, setRunning]           = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState('');
  const [queryName, setQueryName]       = useState('');
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');
  const [savedQueries, setSavedQueries] = useState([]);
  const [showSaved, setShowSaved]       = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [shareModal, setShareModal]     = useState(null);
  const [shareManagers, setShareMgrs]   = useState([]);
  const [shareSelected, setShareSel]    = useState([]);
  const [shareSaving, setShareSaving]   = useState(false);
  const [shareMgrLoading, setShareMgrL] = useState(false);

  const tenantHint = `WHERE tenant_id = '${tenantId}'`;

  const run = async () => {
    if (!sql.trim()) return;
    setRunning(true); setResult(null); setError('');
    try {
      const res = await runSqlQuery(sql, tenantId);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Query failed.');
    } finally { setRunning(false); }
  };

  const handleSave = async () => {
    if (!queryName.trim()) { setSaveMsg('Enter a query name.'); return; }
    if (!sql.trim())       { setSaveMsg('Write a query first.'); return; }
    setSaving(true); setSaveMsg('');
    try {
      await saveSqlQuery({ query_name: queryName, sql_query: sql, sa_tenant: tenantId });
      setSaveMsg(`"${queryName}" saved.`);
      setQueryName('');
    } catch (err) { setSaveMsg(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const loadSavedQueries = async () => {
    try { const res = await getSavedSqlQueries(tenantId); setSavedQueries(res.data.data || []); setShowSaved(true); }
    catch { /* silent */ }
  };

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    setDeleting(id);
    try { await deleteSavedSqlQuery(id, tenantId); setSavedQueries(s => s.filter(q => q.id !== id)); }
    catch { /* silent */ } finally { setDeleting(null); }
  };

  const openShareModal = async (q) => {
    setShareModal(q); setShareSel(q.shared_with_users || []);
    setShareMgrL(true);
    try { const r = await getSharableUsers(tenantId); setShareMgrs(r.data.data || []); }
    catch { /* silent */ } finally { setShareMgrL(false); }
  };

  const handleShareSave = async () => {
    setShareSaving(true);
    try {
      await shareSqlQuery(shareModal.id, shareSelected, tenantId);
      setSavedQueries(s => s.map(q => q.id === shareModal.id ? { ...q, shared_with_users: shareSelected } : q));
      setShareModal(null);
    } catch { /* silent */ } finally { setShareSaving(false); }
  };

  const showing = result?.display_data || [];

  return (
    <div style={{ display:'flex', gap:'0', height:'100%', overflow:'hidden' }}>

      {/* Left: editor */}
      <div style={{ width:'440px', flexShrink:0, borderRight:`1px solid ${D.border}`, overflowY:'auto', padding:'20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
          <div>
            <p style={{ fontSize:'13px', fontWeight:'700', color:D.text, margin:0 }}>SQL Query</p>
            <p style={{ fontSize:'11px', color:D.textMuted, margin:'2px 0 0' }}>SELECT only · results capped at 10k</p>
          </div>
          <button onClick={loadSavedQueries} style={dBtn('secondary')}>📂 Saved</button>
        </div>

        {/* Saved queries */}
        {showSaved && (
          <div style={{ background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'10px', padding:'12px', marginBottom:'14px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
              <p style={{ fontSize:'13px', fontWeight:'600', color:D.text, margin:0 }}>Saved Queries</p>
              <button onClick={() => setShowSaved(false)} style={{ background:'none', border:'none', fontSize:'14px', cursor:'pointer', color:D.textMuted }}>✕</button>
            </div>
            {savedQueries.length === 0
              ? <p style={{ color:D.textMuted, fontSize:'12px', textAlign:'center' }}>No saved queries yet.</p>
              : savedQueries.map(q => (
                <div key={q.id} style={{ padding:'9px 10px', borderRadius:'8px', background:D.surface, marginBottom:'6px', border:`1px solid ${D.border}` }}>
                  <div onClick={() => { setSql(q.sql_query); setQueryName(q.query_name); setShowSaved(false); setResult(null); }} style={{ cursor:'pointer', marginBottom:'6px' }}>
                    <p style={{ fontSize:'13px', fontWeight:'600', color:D.text, margin:0 }}>{q.query_name}</p>
                    <p style={{ fontSize:'10px', color:D.textMuted, margin:'2px 0 0', fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {q.sql_query.substring(0,55)}{q.sql_query.length>55?'…':''}
                    </p>
                  </div>
                  <div style={{ display:'flex', gap:'6px' }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => openShareModal(q)}
                      style={{ ...dBtn(q.shared_with_users?.length>0?'purple':'secondary'), fontSize:'11px', padding:'3px 9px' }}>
                      🔗 {q.shared_with_users?.length>0?`Shared (${q.shared_with_users.length})`:'Share'}
                    </button>
                    <button onClick={(e) => handleDelete(e, q.id, q.query_name)} disabled={deleting===q.id}
                      style={{ padding:'3px 9px', background:'transparent', border:`1px solid ${D.danger}`, borderRadius:'6px', color:D.danger, fontSize:'11px', cursor:'pointer', opacity:deleting===q.id?0.5:1 }}>
                      {deleting===q.id?'...':'🗑'}
                    </button>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        <p style={{ fontSize:'11px', color:D.textMuted, margin:'0 0 6px', fontFamily:'monospace' }}>
          Hint: <span style={{ background:D.elevated, padding:'1px 6px', borderRadius:'4px', color:D.purple }}>{tenantHint}</span>
        </p>
        <textarea
          value={sql} onChange={e => setSql(e.target.value)}
          placeholder={`SELECT sale_date, SUM(payment_amount) AS revenue\nFROM sales\n${tenantHint}\nGROUP BY sale_date\nORDER BY sale_date DESC`}
          style={{ width:'100%', minHeight:'160px', padding:'10px 12px', fontFamily:'monospace', fontSize:'12px',
            background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', outline:'none',
            resize:'vertical', boxSizing:'border-box', color:D.text }} />
        <button onClick={run} disabled={running || !sql.trim()}
          style={{ width:'100%', padding:'11px', marginTop:'10px', background:running||!sql.trim()?D.elevated:D.accent, color:running||!sql.trim()?D.textMuted:'#000', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'700', cursor:running||!sql.trim()?'not-allowed':'pointer' }}>
          {running ? 'Running...' : '▶  Run Query'}
        </button>

        <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
          <input style={{ flex:1, padding:'9px 12px', background:D.elevated, border:`1px solid ${D.border}`, borderRadius:'8px', fontSize:'12px', color:D.text, outline:'none' }}
            placeholder="Query name to save..." value={queryName} onChange={e => setQueryName(e.target.value)} />
          <button onClick={handleSave} disabled={saving}
            style={{ padding:'9px 16px', background:D.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:saving?'not-allowed':'pointer', opacity:saving?0.7:1 }}>
            {saving?'Saving...':'💾 Save'}
          </button>
        </div>
        {saveMsg && <p style={{ fontSize:'12px', marginTop:'6px', color:saveMsg.includes('saved')?D.accent:D.danger }}>{saveMsg}</p>}
        {error && (
          <div style={{ background:'rgba(255,107,107,0.1)', border:`1px solid ${D.danger}`, borderRadius:'8px', padding:'10px 12px', marginTop:'12px', color:D.danger, fontSize:'12px' }}>
            {error}
          </div>
        )}
      </div>

      {/* Right: results */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>
        {!result && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'300px', color:D.textMuted, gap:'12px' }}>
            <p style={{ fontSize:'40px', margin:0 }}>🛢</p>
            <p style={{ fontSize:'13px' }}>Run a query to see results here.</p>
          </div>
        )}
        {result && (
          <div style={{ background:D.surface, border:`1px solid ${D.border}`, borderRadius:'12px', overflow:'hidden' }}>
            <div style={{ padding:'14px 18px', borderBottom:`1px solid ${D.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <p style={{ fontSize:'14px', fontWeight:'700', color:D.text, margin:0 }}>Results</p>
                <p style={{ fontSize:'12px', color:D.textMuted, margin:'2px 0 0' }}>
                  Showing {showing.length} of {result.total_rows} row{result.total_rows!==1?'s':''}
                  {result.capped && <span style={{ color:D.amber }}> · capped at 10,000</span>}
                </p>
              </div>
              <button onClick={() => downloadSqlCSV(result.columns, result.all_data)}
                style={{ padding:'7px 14px', background:D.accentDim, border:`1px solid ${D.accent}`, borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer', color:D.accent }}>
                ⬇ CSV ({result.total_rows})
              </button>
            </div>
            {result.total_rows === 0
              ? <p style={{ textAlign:'center', color:D.textMuted, fontSize:'13px', padding:'32px' }}>No rows returned.</p>
              : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead><tr>{result.columns.map(c => <th key={c} style={{ padding:'9px 12px', textAlign:'left', color:D.textMuted, borderBottom:`1px solid ${D.border}`, background:D.elevated, whiteSpace:'nowrap' }}>{c}</th>)}</tr></thead>
                    <tbody>{showing.map((row,i) => (
                      <tr key={i} style={{ background:i%2===0?'transparent':D.elevated }}>
                        {row.map((cell,j) => <td key={j} style={{ padding:'8px 12px', color:D.textSec }}>
                          {cell===null?<span style={{ color:D.textMuted, fontStyle:'italic' }}>null</span>:String(cell)}
                        </td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )
            }
          </div>
        )}
      </div>

      {shareModal && (
        <DarkShareModal
          title={`Share "${shareModal.query_name}"`}
          note="Managers can run this query but cannot see the SQL:"
          items={shareManagers}
          selected={shareSelected}
          onToggle={(id) => setShareSel(s => s.includes(id)?s.filter(x=>x!==id):[...s,id])}
          onSave={handleShareSave}
          onClose={() => setShareModal(null)}
          saving={shareSaving}
          loading={shareMgrLoading}
        />
      )}
    </div>
  );
};

// ── Main TenantReportBuilder ───────────────────────────────────────────────────
export const TenantReportBuilder = ({ tenant, onClose }) => {
  const [activeTab, setActiveTab] = useState('builder');

  const tabBtn = (key, label) => ({
    padding: '8px 18px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: activeTab === key ? '600' : '400',
    background: activeTab === key ? 'rgba(168,224,99,0.12)' : 'transparent',
    color: activeTab === key ? D.accent : D.textSec,
  });

  return (
    <div style={{ position:'fixed', inset:0, background:D.bg, zIndex:300, display:'flex', flexDirection:'column', fontFamily:"'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ background:D.surface, borderBottom:`1px solid ${D.border}`, padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', height:'56px', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <div>
            <span style={{ fontSize:'14px', fontWeight:'700', color:D.text }}>📊 Report Builder</span>
            <span style={{ marginLeft:'10px', fontSize:'12px', color:D.textMuted }}>
              <span style={{ fontFamily:'monospace', color:D.accent }}>{tenant.tenant_id}</span>
              {tenant.tenant_name && <span> · {tenant.tenant_name}</span>}
            </span>
          </div>
          <div style={{ width:'1px', height:'24px', background:D.border }} />
          <div style={{ display:'flex', gap:'4px' }}>
            <button style={tabBtn('builder', 'Builder')} onClick={() => setActiveTab('builder')}>Builder</button>
            <button style={tabBtn('sql', 'SQL Tool')} onClick={() => setActiveTab('sql')}>🛢 SQL Tool</button>
          </div>
        </div>
        <button onClick={onClose}
          style={{ padding:'7px 16px', background:'rgba(255,107,107,0.15)', border:`1px solid ${D.danger}`, borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer', color:D.danger }}>
          ✕ Close
        </button>
      </div>

      {/* Body */}
      <div style={{ flex:1, overflow:'hidden', display:'flex' }}>
        {activeTab === 'builder' && <TenantBuilderTab tenantId={tenant.tenant_id} />}
        {activeTab === 'sql'     && <TenantSqlTab     tenantId={tenant.tenant_id} />}
      </div>
    </div>
  );
};